import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { fetchWithAuth, getUser, isAuthenticated } from "../../auth/auth";
import LoadingSpinner from "../../components/loadingSpinner/loadingSpinner";
import PillTitle from "../../components/pillTitle/pillTitle";
import { useI18n } from "../../utils/translate/i18n/useI18n";
import { getApiUrl } from "../../utils/getApiUrl";
import UserResultsDetailPanel from "./userResultsChart";
import AnonymousResultsTree from "../../components/anonymousResultsTree/anonymousResultsTree";
import TestResultListItem from "../../components/testResultListItem/testResultListItem";
import "./userResults.scss";
import type { AdminUserOption, AnonymousIpGroup, ParsedResult, TestResultItem, SelectedDetail } from "../../type/result";
import type { VerbalTestResultItem, VerbalTestResultPayload } from "../../type/verbalTest";



function parseResult(raw: string): ParsedResult | null {
  try {
    return JSON.parse(raw) as ParsedResult;
  } catch {
    return null;
  }
}

function parseSqlDate(dateStr: string): Date | null {
  const sqlDateMatch = dateStr.match(
    /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?(?:\.\d{1,9})?(?:Z|[+-]\d{2}:?\d{2})?$/
  );

  if (!sqlDateMatch) {
    return null;
  }

  const [, year, month, day, hour, minute, second = "0"] = sqlDateMatch;

  return new Date(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hour),
    Number(minute),
    Number(second)
  );
}

function formatDate(dateStr: string, locale: string): string {
  const d = parseSqlDate(dateStr) ?? new Date(dateStr);

  if (Number.isNaN(d.getTime())) {
    return dateStr;
  }

  return d.toLocaleString(locale, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit"
  });
}

function isMissingIdentityValue(value?: string | null): boolean {
  return value == null || value.trim() === "" || value.trim().toLowerCase() === "null";
}

function buildAdminUserOptions(items: TestResultItem[]): AdminUserOption[] {
  const groupedUsers = new Map<number, AdminUserOption>();

  items.forEach((item) => {
    if (!Number.isFinite(item.iduser)) {
      return;
    }

    if (groupedUsers.has(item.iduser)) {
      return;
    }

    const label =
      isMissingIdentityValue(item.email) || isMissingIdentityValue(item.username)
        ? "none logged in"
        : `${item.email} + ${item.username}`;

    groupedUsers.set(item.iduser, {
      iduser: item.iduser,
      label
    });
  });

  return Array.from(groupedUsers.values()).sort((left, right) =>
    left.label.localeCompare(right.label, undefined, { sensitivity: "base" })
  );
}

export default function UserResults() {
  const { t, locale } = useI18n();
  const { userId } = useParams<{ userId: string }>();
  const currentUser = getUser();
  const iduser = Number(userId ?? currentUser?.iduser);
  const isAdmin = currentUser?.admin === true;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const directResultId = searchParams.get("resultId") != null ? Number(searchParams.get("resultId")) : null;
  const directVerbalResultId = searchParams.get("verbalResultId") != null ? Number(searchParams.get("verbalResultId")) : null;

  const [results, setResults] = useState<TestResultItem[]>([]);
  const [verbalResults, setVerbalResults] = useState<VerbalTestResultItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAdminPicker, setShowAdminPicker] = useState(false);
  const [adminUserOptions, setAdminUserOptions] = useState<AdminUserOption[]>([]);
  const [adminUsersLoading, setAdminUsersLoading] = useState(false);
  const [adminUsersError, setAdminUsersError] = useState<string | null>(null);
  const [selectedAdminUserId, setSelectedAdminUserId] = useState<number | null>(
    Number.isFinite(iduser) ? iduser : null
  );

  const [showAnonymousPicker, setShowAnonymousPicker] = useState(false);
  const [anonymousGroups, setAnonymousGroups] = useState<AnonymousIpGroup[]>([]);
  const [anonymousLoading, setAnonymousLoading] = useState(false);
  const [anonymousError, setAnonymousError] = useState<string | null>(null);

  const [selectedItem, setSelectedItem] = useState<TestResultItem | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [selectedDetail, setSelectedDetail] = useState<SelectedDetail | null>(null);
  const [listMode, setListMode] = useState<"tone" | "verbal">("tone");
  const activeUserId = showAdminPicker ? selectedAdminUserId : iduser;

  useEffect(() => {
    setSelectedAdminUserId(Number.isFinite(iduser) ? iduser : null);
  }, [iduser]);

  useEffect(() => {
    if (!isAdmin || !showAnonymousPicker || anonymousGroups.length > 0) {
      return;
    }

    setAnonymousLoading(true);
    setAnonymousError(null);

    fetchWithAuth(getApiUrl("testresult/anonymous"))
      .then(async (res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data: TestResultItem[] = await res.json();
        const grouped = data.reduce<Map<string, TestResultItem[]>>((acc, item) => {
          const ip = item.ip_address ?? "unknown";
          if (!acc.has(ip)) acc.set(ip, []);
          acc.get(ip)!.push(item);
          return acc;
        }, new Map());
        setAnonymousGroups(
          Array.from(grouped.entries()).map(([ip_address, items]) => ({ ip_address, items }))
        );
      })
      .catch((err: Error) => setAnonymousError(err.message))
      .finally(() => setAnonymousLoading(false));
  }, [anonymousGroups.length, isAdmin, showAnonymousPicker]);

  useEffect(() => {
    if (!isAdmin || !showAdminPicker || adminUserOptions.length > 0) {
      return;
    }

    setAdminUsersLoading(true);
    setAdminUsersError(null);

    fetchWithAuth(getApiUrl("testresult/all"))
      .then(async (res) => {
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }

        const data: TestResultItem[] = await res.json();
        const options = buildAdminUserOptions(data);
        setAdminUserOptions(options);
        setSelectedAdminUserId((currentSelectedUserId) => {
          if (
            currentSelectedUserId != null &&
            options.some((option) => option.iduser === currentSelectedUserId)
          ) {
            return currentSelectedUserId;
          }

          return options[0]?.iduser ?? null;
        });
      })
      .catch((err) => setAdminUsersError(err.message))
      .finally(() => setAdminUsersLoading(false));
  }, [adminUserOptions.length, isAdmin, showAdminPicker]);

  useEffect(() => {
    if (directResultId != null || directVerbalResultId != null) { setLoading(false); return; }
    if (!isAuthenticated()) {
      navigate("/login");
      return;
    }
    if (activeUserId == null) {
      setResults([]);
      setVerbalResults([]);
      setSelectedItem(null);
      setSelectedDetail(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    setSelectedItem(null);
    setSelectedDetail(null);

    Promise.all([
      fetchWithAuth(getApiUrl(`testresult/user/${activeUserId}`))
        .then(async (res) => {
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          return res.json() as Promise<TestResultItem[]>;
        }),
      fetchWithAuth(getApiUrl(`verbalresult/user/${activeUserId}`))
        .then(async (res) => {
          if (!res.ok) return [] as VerbalTestResultItem[];
          return res.json() as Promise<VerbalTestResultItem[]>;
        })
        .catch(() => [] as VerbalTestResultItem[])
    ])
      .then(([toneData, verbalData]) => {
        setResults(toneData);
        setVerbalResults(verbalData);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [activeUserId, directResultId, directVerbalResultId, navigate]);

  useEffect(() => {
    if (directVerbalResultId == null) return;
    setDetailLoading(true);
    fetchWithAuth(getApiUrl(`verbalresult/${directVerbalResultId}/user/0`))
      .then(async (res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const detail: VerbalTestResultItem = await res.json();
        const parsed = JSON.parse(detail.result) as VerbalTestResultPayload;
        setSelectedDetail({ kind: "verbal", item: detail, parsed });
      })
      .catch((err: unknown) => console.error("Failed to load direct verbal result:", err))
      .finally(() => setDetailLoading(false));
  }, [directVerbalResultId]);

  useEffect(() => {
    if (directResultId == null) return;
    const fetchUserId = 0;
    setDetailLoading(true);
    fetchWithAuth(getApiUrl(`testresult/${directResultId}/user/${fetchUserId}`))
      .then(async (res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const detail: TestResultItem = await res.json();
        setSelectedDetail({ kind: "tone", item: detail, parsed: parseResult(detail.result) });
      })
      .catch((err: unknown) => console.error("Failed to load direct result:", err))
      .finally(() => setDetailLoading(false));
  }, [directResultId]);

  const handleSelect = async (item: TestResultItem) => {
    const resolvedUserId = showAnonymousPicker ? item.iduser : activeUserId;
    if (resolvedUserId == null) {
      return;
    }

    if (selectedItem?.idtonalresult === item.idtonalresult) {
      setSelectedItem(null);
      setSelectedDetail(null);
      return;
    }

    setSelectedItem(item);
    setSelectedDetail(null);
    setDetailLoading(true);

    try {
      const res = await fetchWithAuth(
        getApiUrl(`testresult/${item.idtonalresult}/user/${resolvedUserId}`)
      );
      if (res.status === 401 || res.status === 403) {
        navigate("/login");
        return;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const detail: TestResultItem = await res.json();
      const parsed = parseResult(detail.result);
      setSelectedDetail({ kind: "tone", item, parsed });
    } catch (err) {
      console.error("Failed to load result detail:", err);
      const parsed = parseResult(item.result);
      setSelectedDetail({ kind: "tone", item, parsed });
    } finally {
      setDetailLoading(false);
    }
  };

  const handleSelectVerbal = async (verbalItem: VerbalTestResultItem) => {
    if (activeUserId == null) return;

    if (
      selectedDetail?.kind === "verbal" &&
      selectedDetail.item.idverbalresult === verbalItem.idverbalresult
    ) {
      setSelectedItem(null);
      setSelectedDetail(null);
      return;
    }

    setSelectedItem(null);
    setSelectedDetail(null);
    setDetailLoading(true);

    try {
      const res = await fetchWithAuth(
        getApiUrl(`verbalresult/${verbalItem.idverbalresult}/user/${activeUserId}`)
      );
      if (res.status === 401 || res.status === 403) {
        navigate("/login");
        return;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const detail: VerbalTestResultItem = await res.json();
      const parsed = JSON.parse(detail.result) as VerbalTestResultPayload;
      setSelectedDetail({ kind: "verbal", item: verbalItem, parsed });
    } catch (err) {
      console.error("Failed to load verbal result detail:", err);
      try {
        const parsed = JSON.parse(verbalItem.result) as VerbalTestResultPayload;
        setSelectedDetail({ kind: "verbal", item: verbalItem, parsed });
      } catch {
        setSelectedDetail({ kind: "verbal", item: verbalItem, parsed: null });
      }
    } finally {
      setDetailLoading(false);
    }
  };

  const handleAnalysisSaved = (id: number, kind: "tone" | "verbal", analysis: string) => {
    if (kind === "tone") {
      setResults(prev => prev.map(r =>
        r.idtonalresult === id ? { ...r, ai_analysis: analysis } : r
      ));
      setSelectedItem(prev =>
        prev?.idtonalresult === id ? { ...prev, ai_analysis: analysis } : prev
      );
      setSelectedDetail(prev =>
        prev?.kind === "tone" && prev.item.idtonalresult === id
          ? { ...prev, item: { ...prev.item, ai_analysis: analysis } }
          : prev
      );
    } else {
      setVerbalResults(prev => prev.map(r =>
        r.idverbalresult === id ? { ...r, ai_analysis: analysis } : r
      ));
      setSelectedDetail(prev =>
        prev?.kind === "verbal" && prev.item.idverbalresult === id
          ? { ...prev, item: { ...prev.item, ai_analysis: analysis } }
          : prev
      );
    }
  };

  // Merged list removed — lists are now shown separately per listMode

  if (directResultId != null || directVerbalResultId != null) {
    return (
      <div className="app-content__mainpanel user-results--direct-mode">
        <section className="row">
          <UserResultsDetailPanel
            selection={selectedDetail}
            detailLoading={detailLoading}
            formatDate={(dateStr) => formatDate(dateStr, locale)}
            onAnalysisSaved={handleAnalysisSaved}
          />
        </section>
      </div>
    );
  }

  return (
    <div className="app-content__mainpanel">
      <section className="row">

        <div className="app-content__panelleft">
          <div className="app-content__panelleft__container">
            <div className="card-body p-4 user-results__list-container">
              <div className="user-results__header-row">
                <PillTitle variant="blue-brighter" text={t("Historia badań")} />
                <div className="user-results__header-toggles">
                  <button
                    type="button"
                    className={`user-results__admin-toggle${listMode === "tone" ? " is-active" : ""}`}
                    onClick={() => { setListMode("tone"); setSelectedItem(null); setSelectedDetail(null); }}
                    aria-label={t("Audiometria tonalna")}
                    title={t("Audiometria tonalna")}
                  >
                    <i className="bi bi-ear" />
                  </button>
                  <button
                    type="button"
                    className={`user-results__admin-toggle${listMode === "verbal" ? " is-active" : ""}`}
                    onClick={() => { setListMode("verbal"); setSelectedItem(null); setSelectedDetail(null); }}
                    aria-label={t("Audiometria słowna")}
                    title={t("Audiometria słowna")}
                  >
                    <i className="bi bi-chat-square-text" />
                  </button>
                  {isAdmin && (
                    <>
                      <button
                        type="button"
                        className={`user-results__admin-toggle ${showAnonymousPicker ? "is-active" : ""}`}
                        onClick={() => {
                          setShowAnonymousPicker((v) => !v);
                          setShowAdminPicker(false);
                          setSelectedItem(null);
                          setSelectedDetail(null);
                        }}
                        aria-label={t("Pokaż wyniki niezalogowanych")}
                        title={t("Pokaż wyniki niezalogowanych")}
                      >
                        <i className="bi bi-person-x-fill" />
                      </button>
                      <button
                        type="button"
                        className={`user-results__admin-toggle ${showAdminPicker ? "is-active" : ""}`}
                        onClick={() => {
                          setShowAdminPicker((v) => !v);
                          setShowAnonymousPicker(false);
                          setSelectedItem(null);
                          setSelectedDetail(null);
                        }}
                        aria-label={t("Przełącz wybór użytkownika")}
                        title={t("Przełącz wybór użytkownika")}
                      >
                        <i className="bi bi-people-fill" />
                      </button>
                    </>
                  )}
                </div>
              </div>

              {showAdminPicker ? (
                <div className="mb-4 user-results__admin-picker">
                  <label className="user-results__admin-label" htmlFor="admin-user-select">
                    {t("Użytkownik badań")}
                  </label>
                  <select
                    id="admin-user-select"
                    className="user-results__admin-select"
                    value={selectedAdminUserId ?? ""}
                    onChange={(event) => setSelectedAdminUserId(Number(event.target.value))}
                    disabled={adminUsersLoading || adminUserOptions.length === 0}
                  >
                    {adminUserOptions.length === 0 && (
                      <option value="">{t("Brak użytkowników z badaniami.")}</option>
                    )}
                    {adminUserOptions.map((option) => (
                      <option key={option.iduser} value={option.iduser}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  {adminUsersLoading && (
                    <div className="user-results__admin-note">{t("Ładowanie użytkowników…")}</div>
                  )}
                  {adminUsersError && (
                    <div className="user-results__admin-note user-results__state--error">{adminUsersError}</div>
                  )}
                </div>
              ) : showAnonymousPicker ? (
                <p className="mb-3 user-results__subtitle">
                  {t("Wyniki niezalogowanych — pogrupowane po IP.")}
                </p>
              ) : (
                <>
                  <h2 className="mb-1 user-results__title">
                    {listMode === "verbal" ? t("Audiometria słowna") : t("Audiometria tonalna")}
                  </h2>
                  <p className="mb-4 user-results__subtitle">
                    {t("Wybierz badanie z listy, aby zobaczyć wyniki.")}
                  </p>
                </>
              )}

              {showAnonymousPicker ? (
                <>
                  {anonymousLoading && (
                    <LoadingSpinner className="user-results__state" label={t("Ładowanie…")} />
                  )}
                  {anonymousError && (
                    <div className="user-results__state user-results__state--error">
                      <i className="bi bi-exclamation-triangle-fill me-2" />
                      {anonymousError}
                    </div>
                  )}
                  {!anonymousLoading && !anonymousError && (
                    <AnonymousResultsTree
                      groups={anonymousGroups}
                      selectedItem={selectedItem}
                      onSelect={handleSelect}
                      formatDate={(dateStr) => formatDate(dateStr, locale)}
                    />
                  )}
                </>
              ) : (
                <>
                  {loading && (
                    <LoadingSpinner className="user-results__state" label={t("Ładowanie listy…")}/>
                  )}

                  {error && (
                    <div className="user-results__state user-results__state--error">
                      <i className="bi bi-exclamation-triangle-fill me-2" />
                      {error}
                    </div>
                  )}

                  {!loading && !error && listMode === "tone" && results.length === 0 && (
                    <div className="user-results__state">
                      {t("Brak wyników audiometrii tonalnej.")}
                    </div>
                  )}
                  {!loading && !error && listMode === "verbal" && verbalResults.length === 0 && (
                    <div className="user-results__state">
                      {t("Brak wyników audiometrii słownej.")}
                    </div>
                  )}

                  {!loading && !error && listMode === "tone" && results.length > 0 && (
                    <ul className="user-results__list">
                      {results.map((item, index) => (
                        <TestResultListItem
                          key={`t-${item.idtonalresult}`}
                          item={item}
                          kind="tone"
                          index={results.length - index}
                          isActive={selectedDetail?.kind === "tone" && selectedDetail.item.idtonalresult === item.idtonalresult}
                          formatDate={(dateStr) => formatDate(dateStr, locale)}
                          onClick={() => void handleSelect(item)}
                        />
                      ))}
                    </ul>
                  )}

                  {!loading && !error && listMode === "verbal" && verbalResults.length > 0 && (
                    <ul className="user-results__list">
                      {verbalResults.map((item, index) => (
                        <TestResultListItem
                          key={`v-${item.idverbalresult}`}
                          item={item}
                          kind="verbal"
                          index={verbalResults.length - index}
                          isActive={selectedDetail?.kind === "verbal" && selectedDetail.item.idverbalresult === item.idverbalresult}
                          formatDate={(dateStr) => formatDate(dateStr, locale)}
                          onClick={() => void handleSelectVerbal(item)}
                        />
                      ))}
                    </ul>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        <UserResultsDetailPanel
          selection={selectedDetail}
          detailLoading={detailLoading}
          formatDate={(dateStr) => formatDate(dateStr, locale)}
          onAnalysisSaved={handleAnalysisSaved}
        />

      </section>
    </div>
  );
}
